import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Calendar, Clock, MapPin, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import api from '../services/api';

const InvitationConfirmation = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const action = searchParams.get('action'); // confirm or decline
  const type = searchParams.get('type'); // atelier or action
  const eventId = searchParams.get('event_id');
  const inviteId = searchParams.get('invite_id');
  const token = searchParams.get('token');

  useEffect(() => {
    const processInvitation = async () => {
      if (!action || !type || !eventId || !inviteId || !token) {
        setError('Lien invalide ou incomplet');
        setLoading(false);
        return;
      }

      try {
        const endpoint = action === 'confirm' 
          ? `/invitation/confirm?type=${type}&event_id=${eventId}&invite_id=${inviteId}&token=${token}`
          : `/invitation/decline?type=${type}&event_id=${eventId}&invite_id=${inviteId}&token=${token}`;
        
        const response = await api.get(endpoint);
        setResult(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Une erreur est survenue');
      } finally {
        setLoading(false);
      }
    };

    processInvitation();
  }, [action, type, eventId, inviteId, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md rounded-3xl shadow-xl">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-lg text-slate-600">Traitement en cours...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md rounded-3xl shadow-xl border-red-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Erreur</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isConfirmed = result?.statut === 'confirme';

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${
      isConfirmed 
        ? 'bg-gradient-to-br from-green-50 to-emerald-100' 
        : 'bg-gradient-to-br from-slate-50 to-orange-50'
    }`}>
      <Card className="w-full max-w-md rounded-3xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className={`p-6 text-center text-white ${
          isConfirmed 
            ? 'bg-gradient-to-r from-emerald-500 to-green-600' 
            : 'bg-gradient-to-r from-orange-400 to-red-500'
        }`}>
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            {isConfirmed ? (
              <CheckCircle2 className="w-10 h-10" />
            ) : (
              <XCircle className="w-10 h-10" />
            )}
          </div>
          <h1 className="text-2xl font-bold">
            {isConfirmed ? 'Participation confirmée !' : 'Invitation déclinée'}
          </h1>
        </div>

        {/* Content */}
        <CardContent className="p-6">
          <div className="text-center mb-6">
            <p className="text-slate-600">
              {result?.invite_name && (
                <span className="font-semibold text-slate-800">{result.invite_name}</span>
              )}
              {isConfirmed 
                ? ', votre participation a été enregistrée.'
                : ', nous avons bien pris en compte votre réponse.'}
            </p>
          </div>

          {/* Event Details */}
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <h3 className="font-semibold text-slate-800 mb-3">Détails de l'événement</h3>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Date</p>
                <p className="font-medium text-slate-800">{result?.event_date || '-'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Type</p>
                <p className="font-medium text-slate-800">{result?.event_type || '-'}</p>
              </div>
            </div>

            {result?.event_title && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Événement</p>
                  <p className="font-medium text-slate-800">{result.event_title}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer message */}
          <p className="text-center text-sm text-slate-500 mt-6">
            {isConfirmed 
              ? 'Un email de confirmation vous sera envoyé.' 
              : 'Merci de nous avoir informé.'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvitationConfirmation;
